import React from 'react';
import { useTranslation } from 'react-i18next';
import Chart from '../../../../models/chart';
import PacifaLogo from '../../../../images/pacifa.png';
import {
    ToastContext,
    Form,
    SelectLabel,
    SelectHierarchy,
    WeezHeader,
    Button,
    Chip,
    Card,
    TooltipHelp,
    InputLabel,
    ToggleLabel,
    Input,
    Field,
    ToggleContainer,
    Section
} from '@weezevent/nacre';
import { useForm } from '../../../../utils/validators';
import { usePermissions } from '../../../../user/permissions';

import Style from './chart_settings.module.css';

export default function ChartSettings({ organization, chartId }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const permissions = usePermissions();

    const [submitting, setSubmitting] = React.useState(false);
    const [togglePacifaKey, setTogglePacifaKey] = React.useState(null);
    const [selectedTypePlan, setSelectedTypePlan] = React.useState(null);

    const [form, setForm, setFormObject] = useForm(
        {
            name: null,
            type: 'default',
            metadata: {},
            prevent_orphans_seats: false,
            pacifa_key: null
        },
        {}
    );

    const [chart, loadingChart] = Chart.useApiModel(
        {
            id: chartId,
            organizationId: organization.id,
            launch: Boolean(chartId),
            onSuccess: chart => {
                setFormObject({
                    name: chart.name,
                    prevent_orphans_seats: chart.seatlib_data.prevent_orphans_seats || false,
                    pacifa_key: chart.pacifa_key,
                    type: chart.type,
                    metadata: chart.metadata
                });
                setSelectedTypePlan(chart.type !== 'default' ? 'club' : null);
                setTogglePacifaKey(chart.pacifa_key);
            }
        },
        [chartId, organization, setFormObject]
    );

    const handleSubmit = React.useCallback(() => {
        setSubmitting(true);
        let new_chart = new Chart(chart);
        new_chart.name = form.name;
        new_chart.pacifa_key = form.pacifa_key || null;
        if (!new_chart.seatlib_data) {
            new_chart['seatlib_data'] = {};
        }

        if (selectedTypePlan === 'club') {
            new_chart.type = form.type;
            new_chart.metadata = form.metadata;
        } else {
            new_chart.type = 'default';
            new_chart.metadata = {};
            setForm('type', 'default');
            setForm('metadata', { club_name: '' });
        }

        new_chart.seatlib_data.prevent_orphans_seats = form.prevent_orphans_seats;
        new_chart
            .save({ organizationId: organization.id })
            .then(() => {
                toast.success(t('ticket.charts.newChart.success'));
            })
            .catch(e => {
                console.error(e);
                toast.error(t('ticket.charts.newChart.failure'));
            })
            .finally(() => {
                setSubmitting(false);
            });
    }, [chart, form, organization, selectedTypePlan]);

    const handleClickToggle = React.useCallback(
        (e, { checked }) => {
            setForm('prevent_orphans_seats', checked);
        },
        [setForm]
    );

    const onToggle = React.useCallback(
        checked => {
            if (!checked) {
                setForm('pacifa_key', null);
            }
            setTogglePacifaKey(checked);
        },
        [setForm]
    );

    const isSportsChart = React.useMemo(() => {
        return form.type !== 'default';
    }, [form]);

    const isDisabled = React.useMemo(() => {
        return !form?.name || submitting || (selectedTypePlan === 'club' && form.type === 'default');
    }, [form, submitting, selectedTypePlan]);

    if (loadingChart) {
        return null;
    }
    return (
        <>
            <WeezHeader
                rightComponent={[<Button primary key="save" label={t('ticket.common.action.save')} disabled={isDisabled} onClick={handleSubmit} />]}
                title={t('ticket.charts.settings.title')}
            />

            <Card>
                <Card.Header>
                    <Card.Title title={t('ticket.charts.settings.info-title')} />
                </Card.Header>
                <Card.Content>
                    <Form.Field>
                        <InputLabel label={t('ticket.charts.settings.plan-name')} />
                        <Input
                            onChange={(e, { value }) => setForm('name', value)}
                            value={form?.name}
                            placeholder={t('ticket.charts.contingents.new.contingent-name-placeholder')}
                        />
                    </Form.Field>
                    {(permissions.isAdmin() || isSportsChart) && (
                        <>
                            <SelectLabel
                                label={t('ticket.charts.settings.type-chart')}
                                placeholder={t('ticket.charts.settings.type-chart-placeholder')}
                                onChange={value => {
                                    setSelectedTypePlan(value);
                                }}
                                options={[
                                    { text: t('ticket.charts.settings.type-chart-default'), value: null },
                                    { text: t('ticket.charts.settings.type-chart-sport-club'), value: 'club' }
                                ]}
                                disabled={!permissions.isAdmin()}
                                value={selectedTypePlan}
                            />

                            {selectedTypePlan === 'club' && (
                                <Section>
                                    <Form.Label label={t('ticket.charts.settings.league')} />
                                    <SelectHierarchy
                                        searchable
                                        multi={false}
                                        widthIndent="10px"
                                        onChange={value => setForm('type', value)}
                                        options={[
                                            {
                                                parent: null,
                                                text: t('ticket.charts.settings.league_fr'),
                                                value: null,
                                                children: [
                                                    { key: 0, value: 'lfp_ligue_1', text: t('ticket.charts.settings.league_fr_1') },
                                                    { key: 0, value: 'lfp_ligue_2', text: t('ticket.charts.settings.league_fr_2') }
                                                ]
                                            }
                                        ]}
                                        value={form.type}
                                    />
                                    <Form.Field>
                                        <Form.Label optionalText={t('ticket.common.labelOptional')}>
                                            {t('ticket.charts.settings.club-name')}
                                            <TooltipHelp inlinePosition="left" title={t('ticket.charts.settings.club-name-tooltip')} />
                                        </Form.Label>
                                        <Form.Input onChange={(e, { value }) => setForm('metadata', { club_name: value })} value={form?.metadata?.club_name} />
                                    </Form.Field>
                                </Section>
                            )}
                        </>
                    )}
                    <div className={Style['toggle-container']}>
                        <ToggleLabel
                            inlinePosition="left"
                            label={
                                <div>
                                    {t('ticket.charts.newChart.preventOrphansSeats')}
                                    <TooltipHelp triggerOnHover title={t('ticket.charts.newChart.preventOrphansSeatsPlaceholder')} />
                                </div>
                            }
                            checked={form.prevent_orphans_seats}
                            onChange={handleClickToggle}
                        />
                    </div>
                </Card.Content>
            </Card>

            {permissions.isAdmin() && (
                <Card>
                    <Card.Header>
                        <Card.Title
                            title={
                                <div className={Style['pacifa-title']}>
                                    <div>{t('ticket.charts.settings.pacifa-title')}</div>
                                    <Chip primary text={t('ticket.common.admin')} />
                                </div>
                            }
                        />
                    </Card.Header>
                    <Card.Content>
                        <ToggleContainer
                            className={Style['pacifa-toggle-container']}
                            title={
                                <div>
                                    <div className={Style['pacifa-description-container']}>
                                        <div className={Style['pacifa-toggle-title']}>{t('ticket.charts.settings.pacifa')}</div>
                                        <div className={Style['pacifa-logo-container']}>
                                            <img src={PacifaLogo} alt={PacifaLogo} />
                                        </div>
                                    </div>
                                </div>
                            }
                            toggle={togglePacifaKey}
                            onToggle={onToggle}
                        >
                            <Section className={Style['pacifa-section-container']}>
                                <Field>
                                    <InputLabel label={t('ticket.charts.newChart.pacifaKey')} />
                                    <Input
                                        value={form.pacifa_key}
                                        onChange={(_, { value }) => setForm('pacifa_key', value)}
                                        placeholder={t('ticket.charts.settings.pacifa-placeholder')}
                                    />
                                </Field>
                            </Section>
                        </ToggleContainer>
                    </Card.Content>
                </Card>
            )}
        </>
    );
}
